'use client';

import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SummarizeIcon from '@mui/icons-material/Summarize';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';

type ReportCardDef = {
  key: string;
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
};

const REPORT_CARDS: ReportCardDef[] = [
  {
    key: 'daily-summary',
    title: 'Daily summary',
    description: 'Cash collected per machine, product used, and fill amounts for a location and visit day.',
    href: '/app/toms-vending/reports/daily-summary',
    icon: <SummarizeIcon sx={{ fontSize: 40 }} color="primary" />,
  },
  {
    key: 'site-visits',
    title: 'Site visits',
    description: 'Browse saved site visit snapshots by date range across all locations.',
    href: '/app/toms-vending/reports/site-visits',
    icon: <FactCheckIcon sx={{ fontSize: 40 }} color="primary" />,
  },
  {
    key: 'rejects',
    title: 'Reject report',
    description:
      'Rejected units from site visits (after tallies are saved) and warehouse audits. Filter by date and location.',
    href: '/app/toms-vending/reports/rejects',
    icon: <DeleteOutlineIcon sx={{ fontSize: 40 }} color="primary" />,
  },
  {
    key: 'inferred-sales',
    title: 'Inferred sales',
    description: 'Units sold between visits multiplied by catalog sale prices.',
    href: '/app/toms-vending/reports/inferred-sales',
    icon: <TrendingUpIcon sx={{ fontSize: 40 }} color="primary" />,
  },
  {
    key: 'reconciliation',
    title: 'Cash reconciliation',
    description: 'Compare cash collected at each visit to inferred retail totals.',
    href: '/app/toms-vending/reports/reconciliation',
    icon: <CompareArrowsIcon sx={{ fontSize: 40 }} color="primary" />,
  },
];

function ReportCard({ card, onOpen }: { card: ReportCardDef; onOpen: () => void }) {
  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardActionArea onClick={onOpen} sx={{ height: '100%', display: 'block', textAlign: 'left' }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="flex-start">
            <Box
              sx={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: 1,
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {card.icon}
            </Box>
            <Box minWidth={0} flex={1}>
              <Typography variant="h6" component="div" gutterBottom>
                {card.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.description}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

const TomsVendingReportsPage: React.FC = () => {
  const navigate = useNavigate();

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Reports' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="Reports" />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AssessmentIcon color="action" />
          <Typography variant="body2" color="text.secondary">
            Choose a report to run. More reports will be added here over time.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {REPORT_CARDS.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.key}>
              <ReportCard card={card} onOpen={() => navigate(card.href)} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default TomsVendingReportsPage;
